"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Contact section (item 12): restored from main.html (the earlier build
 * cut it — see the removed note this file replaces in LandingPage.tsx)
 * with modern styling, First/Last name fields, and an "Other topic"
 * option. 2026-08-29 design pass: soft-filled inputs with a lime focus
 * glow, the topic select replaced by tappable chips, a direct-line card
 * on the left, and an honest privacy microline under the CTA.
 * 2026-10-01: the form posts to POST /v1/contact (no account needed), which
 * forwards the message by email to hello@ — it no longer opens the visitor's
 * email app, so it works without one and is the Impressum's second contact
 * channel. The confirmation shows on the page, with the server's receipt id;
 * a failure says so and offers the email address instead (never a fake
 * "sent" state). Spam: a hidden honeypot field plus the time the form was
 * open; no CAPTCHA.
 */

import { useEffect, useId, useRef, useState, type FormEvent } from "react"
import { ArrowRight, CheckCircle2, Mail } from "lucide-react"
import { useI18n } from "@/i18n"
import { Reveal } from "./Reveal"

export type ContactTopic = "general" | "account" | "employer" | "partnership" | "other"

interface ContactSectionProps {
  prefilledTopic?: ContactTopic | null
}

const FIELD_CLASS =
  "min-h-12 w-full rounded-bb-lg border border-transparent bg-cream/70 px-3.5 text-sm text-ink transition-all duration-200 outline-none placeholder:text-muted-foreground/60 hover:bg-cream focus:border-green focus:bg-paper focus:ring-4 focus:ring-lime/30"

const LABEL_CLASS = "text-xs font-bold tracking-wide text-ink-2 uppercase"

/** Same base URL the authenticated API client uses (see api/client.ts). */
export function contactEndpoint(): string {
  const base = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "")
  return `${base}/v1/contact`
}

type SendState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; name: string; email: string; receiptId: string }
  | { kind: "error"; reason: "invalid" | "failed" }

export function ContactSection({ prefilledTopic }: ContactSectionProps) {
  const { t, locale } = useI18n()
  const [topic, setTopic] = useState<ContactTopic>(prefilledTopic ?? "general")
  const [send, setSend] = useState<SendState>({ kind: "idle" })
  const openedAt = useRef(Date.now())
  const confirmationRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (prefilledTopic) setTopic(prefilledTopic)
  }, [prefilledTopic])

  const firstNameId = useId()
  const lastNameId = useId()
  const emailId = useId()
  const topicId = useId()
  const messageId = useId()
  const honeypotId = useId()

  useEffect(() => {
    if (send.kind === "sent") confirmationRef.current?.focus()
  }, [send.kind])

  const topicLabels: Record<ContactTopic, string> = {
    general: t("landingPage.contact.topicGeneral"),
    account: t("landingPage.contact.topicAccount"),
    employer: t("landingPage.contact.topicEmployer"),
    partnership: t("landingPage.contact.topicPartnership"),
    other: t("landingPage.contact.topicOther"),
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (send.kind === "sending") return
    const form = new FormData(event.currentTarget)
    const firstName = String(form.get("firstName") ?? "").trim()
    const lastName = String(form.get("lastName") ?? "").trim()
    const email = String(form.get("email") ?? "").trim()
    const message = String(form.get("message") ?? "").trim()
    const website = String(form.get("website") ?? "")

    setSend({ kind: "sending" })
    try {
      const response = await fetch(contactEndpoint(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          topic,
          message,
          locale: locale === "de" ? "de" : "en",
          website,
          elapsedMs: Math.max(0, Math.round(Date.now() - openedAt.current)),
        }),
      })
      if (response.status === 400) {
        setSend({ kind: "error", reason: "invalid" })
        return
      }
      if (!response.ok) throw new Error(`contact ${response.status}`)
      const body = (await response.json()) as { status?: string; receiptId?: string }
      if (body.status !== "received" || !body.receiptId) throw new Error("contact: unexpected response")
      setSend({ kind: "sent", name: firstName, email, receiptId: body.receiptId })
    } catch {
      setSend({ kind: "error", reason: "failed" })
    }
  }

  function startOver() {
    openedAt.current = Date.now()
    setSend({ kind: "idle" })
  }

  return (
    <section id="contact" className="scroll-mt-[12vh] border-b border-line bg-cream">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 md:grid-cols-[0.9fr_1.1fr] md:px-8">
        <Reveal>
          <p className="text-sm font-semibold tracking-wide text-green uppercase">
            {t("landingPage.contact.eyebrow")}
          </p>
          <h2 className="mt-2 text-3xl font-bold text-ink md:text-4xl">
            {t("landingPage.contact.heading")}
          </h2>
          <p className="mt-3 max-w-prose text-ink-2">{t("landingPage.contact.lead")}</p>
          <div className="mt-8 flex items-start gap-4 rounded-bb-xl border border-green/20 bg-paper p-5">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-green">
              <Mail className="size-5 text-lime" aria-hidden="true" />
            </span>
            <div>
              <p className="font-semibold text-ink">{t("landingPage.contact.directTitle")}</p>
              <p className="mt-1 text-sm leading-relaxed text-ink-2">
                {t("landingPage.contact.directBody")}
              </p>
              <a
                className="mt-2 inline-block text-sm font-semibold text-green underline underline-offset-4 hover:text-ink"
                href={`mailto:${t("landingPage.legal.contactEmail")}`}
              >
                {t("landingPage.legal.contactEmail")}
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal index={1}>
          {send.kind === "sent" ? (
            <div
              ref={confirmationRef}
              tabIndex={-1}
              role="status"
              aria-live="polite"
              className="flex flex-col items-start gap-4 rounded-bb-xl border border-line border-t-4 border-t-lime bg-paper p-6 shadow-[var(--bb-shadow)] outline-none sm:p-8"
            >
              <span className="flex size-11 items-center justify-center rounded-full bg-green">
                <CheckCircle2 className="size-5 text-lime" aria-hidden="true" />
              </span>
              <h3 className="text-xl font-bold text-ink">{t("landingPage.contact.sentTitle")}</h3>
              <p className="text-sm leading-relaxed text-ink-2">
                {t("landingPage.contact.sentBody", { name: send.name, email: send.email })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("landingPage.contact.sentReference", { id: send.receiptId })}
              </p>
              <button
                type="button"
                onClick={startOver}
                className="text-sm font-semibold text-green underline underline-offset-4 hover:text-ink"
              >
                {t("landingPage.contact.sendAnother")}
              </button>
            </div>
          ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5 rounded-bb-xl border border-line border-t-4 border-t-lime bg-paper p-6 shadow-[var(--bb-shadow)] sm:p-8"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor={firstNameId} className={LABEL_CLASS}>
                  {t("landingPage.contact.firstNameLabel")}
                </label>
                <input id={firstNameId} name="firstName" required className={FIELD_CLASS} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor={lastNameId} className={LABEL_CLASS}>
                  {t("landingPage.contact.lastNameLabel")}
                </label>
                <input id={lastNameId} name="lastName" required className={FIELD_CLASS} />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor={emailId} className={LABEL_CLASS}>
                {t("landingPage.contact.emailLabel")}
              </label>
              <input id={emailId} name="email" type="email" required className={FIELD_CLASS} />
            </div>

            <fieldset className="flex flex-col gap-2">
              <legend id={topicId} className={LABEL_CLASS}>
                {t("landingPage.contact.topicLabel")}
              </legend>
              <div role="radiogroup" aria-labelledby={topicId} className="flex flex-wrap gap-2">
                {(Object.keys(topicLabels) as ContactTopic[]).map(key => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={topic === key}
                    onClick={() => setTopic(key)}
                    className={`min-h-10 rounded-bb-full border px-4 text-sm font-semibold transition-all duration-200 ${
                      topic === key
                        ? "border-green bg-green text-paper shadow-sm"
                        : "border-line bg-paper text-ink-2 hover:border-green/50 hover:text-ink"
                    }`}
                  >
                    {topicLabels[key]}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="flex flex-col gap-1.5">
              <label htmlFor={messageId} className={LABEL_CLASS}>
                {t("landingPage.contact.messageLabel")}
              </label>
              <textarea
                id={messageId}
                name="message"
                required
                rows={5}
                className={`${FIELD_CLASS} py-2`}
              />
            </div>

            {/* Honeypot: off-screen and out of the tab order, so people never fill it; naive bots do. */}
            <div aria-hidden="true" className="absolute -left-[10000px] size-px overflow-hidden">
              <label htmlFor={honeypotId}>Website</label>
              <input id={honeypotId} name="website" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            <button
              type="submit"
              disabled={send.kind === "sending"}
              className="group mt-1 flex min-h-12 items-center justify-center gap-2 rounded-bb-full bg-green px-6 text-sm font-bold text-paper shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green disabled:cursor-wait disabled:opacity-70 disabled:hover:translate-y-0"
            >
              {send.kind === "sending" ? t("landingPage.contact.sending") : t("landingPage.contact.submitCta")}
              <ArrowRight
                className="size-4 transition-transform duration-200 group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </button>

            <p className="text-center text-xs text-muted-foreground">
              {t("landingPage.contact.privacyHint")}
            </p>
            <p role="status" aria-live="polite" className="min-h-4 text-center text-xs text-destructive">
              {send.kind === "error" && send.reason === "invalid" && t("landingPage.contact.invalidBody")}
              {send.kind === "error" && send.reason === "failed" && (
                <>
                  {t("landingPage.contact.errorBody")}{" "}
                  <a className="font-semibold underline" href={`mailto:${t("landingPage.legal.contactEmail")}`}>
                    {t("landingPage.legal.contactEmail")}
                  </a>
                  .
                </>
              )}
            </p>
          </form>
          )}
        </Reveal>
      </div>
    </section>
  )
}
